import { BACKOFFICE_ROLE_IDS, roleRoutes } from "./roles";
import { CLIENT_MANAGER_ROLE_ID } from "../config/menuConfig";

// Quién puede editar o cancelar un pedido según su estado.
//
// Es la ÚNICA fuente de estas reglas: la usan el menú de cada fila de Pedidos
// (ModernOrdersList) y el detalle del pedido (EvidenceView). Antes vivían como
// expresiones sueltas en la lista; con el detalle ofreciendo las mismas acciones,
// dos copias terminarían divergiendo y un pedido se podría cancelar desde un lado
// y no desde el otro.
//
// Son reglas de INTERFAZ: el backend tiene que validar igual.
//
// El comercio (rol client) no edita: el panel de edición cambia estado y
// repartidor, que son decisiones del operador logístico. Puede cancelar mientras
// el pedido no salió de su bodega (pendiente o con la etiqueta ya impresa). El
// resto de los roles edita mientras el pedido no esté entregado ni cancelado, y
// cancela solo si sigue pendiente.

const STATUS_PENDING = 0;
const STATUS_DELIVERED = 4;
const STATUS_CANCELLED = 5;
const STATUS_LABEL_PRINTED = 100;

const isClientRole = (role) => role === roleRoutes.client;

export function canEditOrder(role, status) {
  if (isClientRole(role)) return false;
  return status !== STATUS_DELIVERED && status !== STATUS_CANCELLED;
}

export function canCancelOrder(role, status) {
  if (isClientRole(role)) return status === STATUS_PENDING || status === STATUS_LABEL_PRINTED;
  return status === STATUS_PENDING;
}

// Pedido de cambio de producto (envío hijo que lleva el reemplazo y retira el
// original). Lo crean y consultan el comercio dueño y el back-office del operador;
// nunca un repartidor, ni el holding ni el superadmin del sistema. Es la misma
// lista blanca de roles que `canAccessExchange` de microservice-packages, que es
// quien decide (404 a quien no es dueño del padre).
const EXCHANGE_OPERATOR_ROLES = [
  roleRoutes.admin,
  roleRoutes.superadmin,
  roleRoutes.supervisor,
  roleRoutes.support,
];

/** true si el rol puede leer GET /packages/:id/exchanges (al resto el backend le da 404). */
export function canViewExchanges(role) {
  return isClientRole(role) || EXCHANGE_OPERATOR_ROLES.includes(role);
}

export function canCreateExchange(role, pkg) {
  if (pkg?.status !== STATUS_DELIVERED) return false;
  if (!canViewExchanges(role)) return false;
  if (isClientRole(role)) return true;
  // Una entrega de la flota propia del comercio es del comercio, no del operador.
  return pkg?.currentCarrier?.carrierType !== "commerce_fleet";
}

// Editar la dirección de entrega (PATCH packages/:id/delivery-address). Espejo
// del backend, que es la autoridad: solo mientras el pedido no sale a reparto
// (pendiente, etiqueta impresa, recolectado, en bodega). Desde que lo tiene el
// repartidor, la dirección la ajusta solo él desde la app.
//
// Pueden el comercio (incluye al vendedor, que es un usuario de comercio con la
// marca isSeller), el holding y el personal del operador (los mismos roles que
// INCIDENT_STAFF_ROLE_IDS en microservice-packages). Quedan fuera los
// repartidores, la flota propia del comercio y el display de Centro de Control.
const STATUS_PICKED_UP = 1;
const STATUS_IN_WAREHOUSE = 2;
const ADDRESS_EDITABLE_STATUSES = [STATUS_PENDING, STATUS_LABEL_PRINTED, STATUS_PICKED_UP, STATUS_IN_WAREHOUSE];
const ADDRESS_EDIT_ROLE_IDS = [
  roleRoutes.client,
  CLIENT_MANAGER_ROLE_ID,
  roleRoutes.holdingAdmin,
  ...BACKOFFICE_ROLE_IDS,
];

export function canEditDeliveryAddress(role, status) {
  return ADDRESS_EDIT_ROLE_IDS.includes(role) && ADDRESS_EDITABLE_STATUSES.includes(status);
}
